/**
 * /de/produkt-roadmap — PM concept page (DE, single-locale).
 *
 * Targets produkt roadmap (90) / projekt roadmap / roadmap projektmanagement.
 * Concept + the drift reframe (Roadmap, die mit Entscheidungen verknüpft ist).
 */
import { MarketingShell } from './MarketingShell';

export function DeProduktRoadmap() {
  return (
    <MarketingShell
      currentLocale="de"
      hreflangPath="/produkt-roadmap"
      hreflangLocales={['de']}
      documentTitle="Produkt-Roadmap: Vorlage, Beispiel & Best Practices · Draftlize"
      documentDescription="So baust du eine klare Produkt-Roadmap: Formate, Beispiel und wie du sie mit Entscheidungen verknüpfst, damit sie aktuell bleibt, wenn sich der Scope ändert."
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">Leitfaden · Roadmap</span>
          <h1 className="serif" style={{ fontSize: 'clamp(36px, 4.8vw, 58px)', lineHeight: 1.05, margin: '12px 0 24px' }}>
            Die Produkt-Roadmap,<br />
            <em>die Scope-Änderungen überlebt.</em>
          </h1>
          <p className="lede">
            Eine Produkt-Roadmap zeigt, <em>wohin</em> das Produkt geht und <em>warum</em> – nicht jedes Detail, sondern Richtung und Reihenfolge. Das Problem ist selten das Erstellen. Es ist, dass die Roadmap als eingefrorene Folie endet: drei Wochen später hat sich eine Entscheidung geändert, die Roadmap nicht, und niemand traut ihr mehr. Hier: Formate, ein Beispiel – und wie du die Roadmap mit den Entscheidungen verknüpfst, damit sie wahr bleibt.
          </p>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">Roadmap-Formate, die funktionieren.</h2>
        </div>
        <div style={{ maxWidth: 760 }}>
          <ul style={{ fontSize: 16, lineHeight: 2, color: 'var(--ink-1)', margin: 0, paddingLeft: 20 }}>
            <li><strong>Now / Next / Later</strong> – ohne falsche Datums-Präzision, ideal für früh.</li>
            <li><strong>Themen- bzw. outcome-basiert</strong> – nach Zielen statt Features.</li>
            <li><strong>Zeitbasiert (Quartale)</strong> – wenn Abhängigkeiten harte Termine erzwingen.</li>
          </ul>
          <p style={{ fontSize: 16, lineHeight: 1.8, color: 'var(--ink-1)', margin: '20px 0 0' }}>
            <strong>Mini-Beispiel (Now/Next/Later):</strong> Now – Onboarding-Reibung senken · Next – Self-Serve-Abrechnung · Later – Team-Workspaces. Jeder Eintrag trägt ein <em>Warum</em>: die Entscheidung, die ihn auf die Roadmap gebracht hat. Genau dieses Warum geht in der Folien-Version verloren.
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">Eingefroren vs. lebendig</span>
            <h2 className="serif">Folien-Roadmap <em>vs. Roadmap, die mit Entscheidungen lebt.</em></h2>
          </div>
          <p>Eine Roadmap ist nur das sichtbare Ende einer Kette von Entscheidungen. Trennt man beide, veraltet die Roadmap leise.</p>
        </div>
        <table className="compare-table">
          <thead>
            <tr>
              <th></th>
              <th>Folien-Roadmap</th>
              <th className="ours">Verknüpfte Roadmap (Draftlize)</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>Das „Warum“ je Eintrag</td><td className="no">In Köpfen / verstreut</td><td className="ours yes">Als Entscheidungskarte verlinkt</td></tr>
            <tr><td>Eine Entscheidung ändert sich</td><td className="no">Roadmap bleibt falsch</td><td className="ours yes">Betroffene Einträge werden „stale“</td></tr>
            <tr><td>Drei Wochen später</td><td className="no">Niemand traut ihr</td><td className="ours yes">Aktuell oder sichtbar markiert</td></tr>
            <tr><td>KI-Agent kann sie nutzen</td><td className="partial">Liest flache Folie</td><td className="ours yes">Liest Kontext via MCP</td></tr>
          </tbody>
        </table>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">Roadmap und PRD gehören zusammen.</h2>
          <p style={{ maxWidth: 720, marginTop: 12, color: 'var(--ink-1)', fontSize: 16 }}>
            Ein Roadmap-Eintrag wird irgendwann ein <a href="/de/prd-anforderungsdokument" style={{ color: 'var(--ink-0)', textDecoration: 'underline' }}>PRD</a>. Wenn beide als verknüpfte Karten leben statt als getrennte Dokumente, kippt eine geänderte Entscheidung beide gemeinsam – und nichts driftet still auseinander.
          </p>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          Die meisten Roadmaps sind nicht falsch geplant – sie sind nur drei Wochen nach der letzten Entscheidung nicht mehr nachgezogen worden.
        </blockquote>
        <cite>Verknüpfe die Roadmap mit den Entscheidungen, dann zieht sie automatisch nach.</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">FAQ</span>
            <h2 className="serif">Häufige Fragen.</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820 }}>
          {[
            { q: 'Roadmap oder Backlog – was ist der Unterschied?', a: 'Die Roadmap zeigt Richtung und Reihenfolge auf Outcome-Ebene; der Backlog ist die priorisierte Liste konkreter Arbeit. Die Roadmap beantwortet „warum/wohin“, der Backlog „was als Nächstes“.' },
            { q: 'Welcher Zeithorizont gehört auf eine Roadmap?', a: 'So weit, wie ihr ehrlich Richtung geben könnt – oft Now/Next/Later statt fixer Quartale. Falsche Datums-Präzision ist der Hauptgrund, warum Roadmaps unglaubwürdig werden.' },
            { q: 'Wie gehe ich mit Änderungen um?', a: 'Ändert sich die Entscheidung hinter einem Eintrag, muss der Eintrag mit. In Draftlize sind beide verknüpft: Die geänderte Entscheidung markiert die betroffenen Roadmap-Teile automatisch als „stale“.' },
            { q: 'Wer besitzt die Roadmap?', a: 'In der Regel das Produktmanagement, in Abstimmung mit Engineering und Stakeholdern. Wichtiger als der Owner ist, dass das „Warum“ jedes Eintrags auffindbar bleibt.' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.8, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">Eine Roadmap, die nachzieht <em>– gratis mit $5.</em></h2>
        </div>
        <div className="trust-strip">
          <span>$5 Gratis-Guthaben für neue Accounts</span>
          <span>Zahle nur, was du nutzt</span>
          <span>Guthaben verfällt nie</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            Baue deine Roadmap als verknüpfte Karten statt als Folie. Ändert sich eine Entscheidung, ziehen Roadmap und PRD automatisch nach – nicht drei Wochen später.
          </p>
          <a className="btn-primary" href="/">Gratis mit $5 starten</a>
        </div>
      </section>
    </MarketingShell>
  );
}
